import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import http from '../api/http';
import WideModal from '../components/WideModal';
import { useToasts } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const toDateRangeLabel = (row) => {
  const start = row?.training_start_date ? String(row.training_start_date).slice(0, 10) : '';
  const end = row?.training_end_date ? String(row.training_end_date).slice(0, 10) : '';
  if (start && end) return `${start} ~ ${end}`;
  if (start) return start;
  if (end) return end;
  return '—';
};

const statusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return '已完成';
  if (s === 'pending' || !s) return '未完成';
  return status;
};

const toPositiveNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

export default function TrainingRecordsPage({ addToast: addToastProp }) {
  const toastCtx = useToasts?.();
  const addToast = addToastProp || toastCtx?.addToast;
  const auth = useAuth?.();
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(() => {
    try {
      return window.innerWidth <= 768;
    } catch (e) {
      return false;
    }
  });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [signOpen, setSignOpen] = useState(false);
  const [signingRow, setSigningRow] = useState(null);
  const [signSaving, setSignSaving] = useState(false);

  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPlanRow, setSessionsPlanRow] = useState(null);
  const [sessions, setSessions] = useState([]);

  const canvasRef = useRef(null);
  const drawingRef = useRef({ drawing: false, lastX: 0, lastY: 0 });

  const sectionTitleStyle = { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 .75rem', color: 'var(--text-primary)' };
  const hintStyle = { fontSize: 12, color: 'var(--text-secondary)', marginTop: '.75rem', lineHeight: 1.5 };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/education-training/training-records');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[training-records-page] fetch failed:', err);
      addToast?.(err.response?.data?.message || '获取培训记录失败', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    const onResize = () => {
      try {
        setIsMobile(window.innerWidth <= 768);
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobilePortalRoute = useMemo(() => {
    const p = String(location?.pathname || '');
    return p === '/m' || p.startsWith('/m/');
  }, [location?.pathname]);

  const mobileMode = isMobile || isMobilePortalRoute;

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows.slice() : [];
    list.sort((a, b) => {
      const ad = a?.training_start_date ? String(a.training_start_date) : '';
      const bd = b?.training_start_date ? String(b.training_start_date) : '';
      if (ad === bd) return Number(b?.plan_id || 0) - Number(a?.plan_id || 0);
      return bd.localeCompare(ad);
    });
    return list;
  }, [rows]);

  const rowMapByPlanId = useMemo(() => {
    const map = {};
    (rows || []).forEach((row) => {
      const key = String(row?.plan_id || '');
      if (key) map[key] = row;
    });
    return map;
  }, [rows]);

  const calcStudyHours = useCallback((planId) => {
    if (!planId) return 0;
    const row = rowMapByPlanId[String(planId)];
    const seconds = toPositiveNumber(row?.study_seconds);
    return Math.round((seconds / 3600) * 100) / 100;
  }, [rowMapByPlanId]);

  const requiredHoursFor = useCallback((row) => {
    const planned = toPositiveNumber(row?.planned_hours);
    if (planned > 0) return planned;
    return toPositiveNumber(row?.training_hours);
  }, []);

  const canSignByStudyHours = useCallback((row) => {
    const required = requiredHoursFor(row);
    if (required <= 0) return true;
    return calcStudyHours(row?.plan_id) >= required;
  }, [calcStudyHours, requiredHoursFor]);

  const startLearning = useCallback((row) => {
    if (!row?.plan_id) {
      addToast?.('无效的培训计划', 'warning');
      return;
    }
    const target = isMobilePortalRoute
      ? `/m/me/training-records/learn/${row.plan_id}`
      : `/education-training/records/learn/${row.plan_id}`;
    navigate(target);
  }, [addToast, isMobilePortalRoute, navigate]);

  const goToExam = useCallback((row) => {
    const examId = Number(row?.exam_id || 0);
    if (!examId) {
      addToast?.('该培训计划未关联考试', 'warning');
      return;
    }
    if (!canSignByStudyHours(row)) {
      addToast?.('学习学时未达标，暂不能参加考试', 'warning');
      return;
    }
    navigate(`/exam-system?examId=${examId}&planId=${Number(row?.plan_id || 0)}&autoStart=1`);
  }, [addToast, canSignByStudyHours, navigate]);

  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const cssWidth = parent ? parent.clientWidth : 320;
    const cssHeight = 220;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssWidth * ratio));
    canvas.height = Math.max(1, Math.floor(cssHeight * ratio));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cssWidth = Number(String(canvas.style.width || '').replace('px', '')) || 320;
    const cssHeight = Number(String(canvas.style.height || '').replace('px', '')) || 220;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
  };

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = event.touches && event.touches[0];
    const clientX = isTouch ? event.touches[0].clientX : event.clientX;
    const clientY = isTouch ? event.touches[0].clientY : event.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault?.();
    const p = getPoint(e);
    drawingRef.current = { drawing: true, lastX: p.x, lastY: p.y };
  };

  const moveDraw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!drawingRef.current.drawing) return;
    e.preventDefault?.();
    const p = getPoint(e);
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.lastX, drawingRef.current.lastY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawingRef.current.lastX = p.x;
    drawingRef.current.lastY = p.y;
  };

  const endDraw = (e) => {
    e.preventDefault?.();
    drawingRef.current.drawing = false;
  };

  const openSign = (row) => {
    setSigningRow(row);
    setSignOpen(true);
    setTimeout(() => {
      fitCanvas();
    }, 0);
  };

  const saveSignature = async () => {
    if (!signingRow?.plan_id || !signingRow?.employee_id || !signingRow?.session_id) return;
    if (!canSignByStudyHours(signingRow)) {
      addToast?.('学习学时未达标，暂不能签名', 'warning');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSignSaving(true);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      await http.put(
        `/education-training/training-plans/${signingRow.plan_id}/participants/${signingRow.employee_id}/sessions/${signingRow.session_id}/signature`,
        { signature_data: dataUrl }
      );
      addToast?.('场次签名已保存', 'success');
      setSignOpen(false);
      setSigningRow(null);
      // 刷新场次列表与主表统计
      if (sessionsPlanRow) {
        await openSessions(sessionsPlanRow);
      }
      await fetchRecords();
    } catch (err) {
      console.error('[training-records-page] save signature failed:', err);
      addToast?.(err.response?.data?.message || '保存场次签名失败', 'error');
    } finally {
      setSignSaving(false);
    }
  };

  const openSessions = useCallback(
    async (row) => {
      if (!row?.plan_id || !row?.employee_id) return;
      if (!canSignByStudyHours(row)) {
        addToast?.('请先点击“开始学习”，学时达标后再签名', 'warning');
        return;
      }
      setSessionsPlanRow(row);
      setSessionsOpen(true);
      setSessionsLoading(true);
      try {
        const res = await http.get(
          `/education-training/training-plans/${row.plan_id}/participants/${row.employee_id}/sessions`
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setSessions(list);
      } catch (err) {
        console.error('[training-records-page] fetch sessions failed:', err);
        addToast?.(err.response?.data?.message || '获取场次列表失败', 'error');
        setSessions([]);
      } finally {
        setSessionsLoading(false);
      }
    },
    [addToast, canSignByStudyHours]
  );

  const sessionRequiredHours = useMemo(() => requiredHoursFor(sessionsPlanRow), [requiredHoursFor, sessionsPlanRow]);
  const sessionLearnedHours = useMemo(() => calcStudyHours(sessionsPlanRow?.plan_id), [calcStudyHours, sessionsPlanRow?.plan_id]);
  const sessionSignUnlocked = useMemo(() => canSignByStudyHours(sessionsPlanRow), [canSignByStudyHours, sessionsPlanRow]);

  return (
    <div>
      <h2 style={sectionTitleStyle}>培训记录（个人）</h2>
      <div style={{ fontSize: 12, color: '#64748b', margin: '-2px 0 10px', lineHeight: 1.5 }}>
        姓名：{auth?.user?.name || auth?.user?.username || '—'} ｜ 打印日期：{new Date().toISOString().slice(0, 10)}
      </div>

      <style>
        {`@media print {
          .trp-controls { display: none !important; }
          .trp-card { box-shadow: none !important; border: none !important; }
          .trp-table { border: 1px solid #111827 !important; }
          body { background: #fff !important; }
        }`}
      </style>

      <div className="trp-controls" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          onClick={fetchRecords}
          disabled={loading}
          style={{
            padding: '.55rem 1rem',
            fontSize: 13,
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            border: '1px solid var(--border-color)',
            background: 'var(--bg-tertiary)',
            color: 'var(--text-primary)'
          }}
        >
          {loading ? '刷新中...' : '刷新'}
        </button>
        <button
          onClick={() => window.print()}
          className={isMobile ? 'trp-print-btn' : undefined}
          style={{
            padding: '.55rem 1rem',
            fontSize: 13,
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            border: '1px solid var(--border-color)',
            background: 'var(--bg-tertiary)',
            color: 'var(--text-primary)'
          }}
        >
          打印
        </button>
        <div style={{ fontSize: 12, color: '#64748b' }}>共 {rows.length} 条记录</div>
      </div>

      <style>
        {`@media (max-width: 768px) {
          .trp-controls { gap: 8px !important; }
          .trp-print-btn { display: none !important; }
        }`}
      </style>

      {loading ? (
        <div>正在加载...</div>
      ) : sortedRows.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
          暂无培训记录。若已被纳入参培人员，请联系管理员确认账号是否绑定员工信息（employee_id）。
        </div>
      ) : mobileMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sortedRows.map((r) => (
            (() => {
              const requiredHours = requiredHoursFor(r);
              const studiedHours = calcStudyHours(r.plan_id);
              const canSign = canSignByStudyHours(r);
              return (
            <div
              key={`${r.plan_id}_${r.employee_id}`}
              style={{
                background: '#fff',
                border: '1px solid var(--border-color)',
                borderRadius: 12,
                padding: 12,
                boxShadow: '0 6px 16px rgba(15,23,42,0.05)'
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{r.topic || '—'}</div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>时间：{toDateRangeLabel(r)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>{statusLabel(r.status)}</div>
              </div>

              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  应训/登记：<span style={{ fontWeight: 600 }}>{r.planned_hours ?? '—'} / {r.completed_hours ?? '—'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  成绩：<span style={{ fontWeight: 600 }}>{r.score ?? '—'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  分次：<span style={{ fontWeight: 600 }}>{Number(r.session_count || 0)} 次</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  场次签名：<span style={{ fontWeight: 600 }}>{Number(r.signed_session_count || 0)} / {Number(r.session_count || 0)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  学习学时：<span style={{ fontWeight: 600 }}>{studiedHours} / {requiredHours || 0}</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155' }}>
                  关联考试：<span style={{ fontWeight: 600 }}>{r.exam_title || (r.exam_id ? `#${r.exam_id}` : '—')}</span>
                </div>
              </div>

              {r.completed_at ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>完成日期：{String(r.completed_at).slice(0, 10)}</div>
              ) : null}
              {r.last_session_signed_at ? (
                <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                  最近签名：{String(r.last_session_signed_at).slice(0, 19).replace('T', ' ')}
                </div>
              ) : null}

              <div className="trp-controls" style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => startLearning(r)}
                  style={{
                    padding: '.55rem 1rem',
                    fontSize: 13,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    flex: '1 1 140px'
                  }}
                >
                  开始学习
                </button>
                {canSign ? (
                  <button
                    type="button"
                    onClick={() => openSessions(r)}
                    style={{
                      padding: '.55rem 1rem',
                      fontSize: 13,
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600,
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                      flex: '1 1 140px'
                    }}
                  >
                    签名场次
                  </button>
                ) : (
                  <div
                    style={{
                      padding: '.55rem .75rem',
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px dashed var(--border-color)',
                      color: '#64748b',
                      flex: '1 1 140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    学时未达标，暂不可签名
                  </div>
                )}
                <button
                  type="button"
                  onClick={fetchRecords}
                  style={{
                    padding: '.55rem 1rem',
                    fontSize: 13,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    flex: '1 1 140px'
                  }}
                >
                  刷新
                </button>
                {r.exam_id ? (
                  <button
                    type="button"
                    onClick={() => goToExam(r)}
                    disabled={!canSign}
                    style={{
                      padding: '.55rem 1rem',
                      fontSize: 13,
                      borderRadius: 8,
                      cursor: canSign ? 'pointer' : 'not-allowed',
                      fontWeight: 600,
                      border: '1px solid var(--border-color)',
                      background: canSign ? 'var(--bg-tertiary)' : '#f1f5f9',
                      color: canSign ? 'var(--text-primary)' : '#94a3b8',
                      flex: '1 1 140px'
                    }}
                    title={canSign ? '进入考试' : '学习学时未达标，暂不能考试'}
                  >
                    进入考试
                  </button>
                ) : null}
              </div>
            </div>
              );
            })()
          ))}

          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginTop: 4 }}>
            说明：移动端隐藏“打印”按钮；如需打印，请在电脑端打开本页后点击“打印”。
          </div>
        </div>
      ) : (
        <div className="trp-card" style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12, padding: 12, boxShadow: '0 6px 16px rgba(15,23,42,0.05)', overflow: 'auto' }}>
          <table className="trp-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>培训主题</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>时间</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>应训/登记</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>分次</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>场次签名</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>成绩</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>状态</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>完成日期</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={`${r.plan_id}_${r.employee_id}`}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#0f172a' }}>{r.topic || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{toDateRangeLabel(r)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>
                    {r.planned_hours ?? '—'} / {r.completed_hours ?? '—'}
                    <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                      学习学时：{calcStudyHours(r.plan_id)} / {requiredHoursFor(r) || 0}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                      关联考试：{r.exam_title || (r.exam_id ? `#${r.exam_id}` : '—')}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{Number(r.session_count || 0)} 次</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>
                    {Number(r.signed_session_count || 0)} / {Number(r.session_count || 0)}
                    {r.last_session_signed_at ? (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                        最近签名：{String(r.last_session_signed_at).slice(0, 19).replace('T', ' ')}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{r.score ?? '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{statusLabel(r.status)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{r.completed_at ? String(r.completed_at).slice(0, 10) : '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        className="trp-controls"
                        type="button"
                        onClick={() => startLearning(r)}
                        style={{
                          padding: '.45rem .75rem',
                          fontSize: 12,
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontWeight: 600,
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        开始学习
                      </button>
                      {canSignByStudyHours(r) ? (
                        <button
                          className="trp-controls"
                          type="button"
                          onClick={() => openSessions(r)}
                          style={{
                            padding: '.45rem .75rem',
                            fontSize: 12,
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 600,
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-tertiary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          签名场次
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: '#64748b' }}>学时未达标</span>
                      )}
                      {r.exam_id ? (
                        <button
                          className="trp-controls"
                          type="button"
                          onClick={() => goToExam(r)}
                          disabled={!canSignByStudyHours(r)}
                          style={{
                            padding: '.45rem .75rem',
                            fontSize: 12,
                            borderRadius: 6,
                            cursor: canSignByStudyHours(r) ? 'pointer' : 'not-allowed',
                            fontWeight: 600,
                            border: '1px solid var(--border-color)',
                            background: canSignByStudyHours(r) ? 'var(--bg-tertiary)' : '#f1f5f9',
                            color: canSignByStudyHours(r) ? 'var(--text-primary)' : '#94a3b8'
                          }}
                          title={canSignByStudyHours(r) ? '进入考试' : '学习学时未达标，暂不能考试'}
                        >
                          进入考试
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WideModal
        isOpen={sessionsOpen}
        onClose={() => {
          if (signSaving) return;
          setSessionsOpen(false);
          setSessionsPlanRow(null);
          setSessions([]);
        }}
        title={`场次列表：${sessionsPlanRow?.topic || ''}`}
        size={mobileMode ? 'md' : 'lg'}
        bodyStyle={{ maxHeight: '75vh' }}
      >
        {!sessionsLoading && sessionsPlanRow ? (
          <div style={{ marginBottom: 10, fontSize: 12, color: sessionSignUnlocked ? '#16a34a' : '#b45309' }}>
            学习学时：{sessionLearnedHours} / {sessionRequiredHours || 0}
            {sessionSignUnlocked ? '（已达标，可签名）' : '（未达标，签名按钮隐藏）'}
          </div>
        ) : null}
        {sessionsLoading ? (
          <div>正在加载...</div>
        ) : sessions.length === 0 ? (
          <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
            暂无场次记录（请由管理员在“岗中台账/分次登记”中维护场次）。
          </div>
        ) : mobileMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map((s) => {
              const hasSig = Boolean(s.signature_data);
              return (
                <div
                  key={s.id}
                  style={{
                    background: '#fff',
                    border: '1px solid var(--border-color)',
                    borderRadius: 12,
                    padding: 12
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                        {s.session_date ? String(s.session_date).slice(0, 10) : '—'}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>学时：{s.hours ?? '—'} ｜ 备注：{s.remark || '—'}</div>
                    </div>
                    <div style={{ fontSize: 12, color: hasSig ? '#16a34a' : '#64748b', whiteSpace: 'nowrap' }}>
                      {hasSig ? '已签名' : '未签名'}
                    </div>
                  </div>

                  {hasSig ? (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <img
                        src={s.signature_data}
                        alt="signature"
                        style={{ height: 34, maxWidth: 140, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 4 }}
                      />
                      <div style={{ fontSize: 12, color: '#64748b' }}>{s.signer_name || ''}</div>
                      {s.signed_at ? (
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{String(s.signed_at).slice(0, 19).replace('T', ' ')}</div>
                      ) : null}
                    </div>
                  ) : null}

                  {sessionSignUnlocked ? (
                    <div className="trp-controls" style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() =>
                          openSign({
                            plan_id: sessionsPlanRow.plan_id,
                            employee_id: sessionsPlanRow.employee_id,
                            session_id: s.id,
                            session_date: s.session_date ? String(s.session_date).slice(0, 10) : '',
                            topic: sessionsPlanRow.topic
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '.7rem 1rem',
                          fontSize: 14,
                          borderRadius: 10,
                          cursor: 'pointer',
                          fontWeight: 700,
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        {hasSig ? '重签该场次' : '签名该场次'}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ overflow: 'auto', background: '#fff', border: '1px solid var(--border-color)', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>日期</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>学时</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>备注</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>签名</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 160 }}>{s.session_date ? String(s.session_date).slice(0, 10) : '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 120 }}>{s.hours ?? '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{s.remark || '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {s.signature_data ? (
                          <img
                            src={s.signature_data}
                            alt="signature"
                            style={{ height: 30, maxWidth: 110, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 4 }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, color: '#64748b' }}>未签名</span>
                        )}
                        <span style={{ fontSize: 12, color: '#64748b' }}>{s.signer_name || ''}</span>
                        {s.signed_at ? (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{String(s.signed_at).slice(0, 19).replace('T', ' ')}</span>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', width: 140 }}>
                      {sessionSignUnlocked ? (
                        <button
                          className="trp-controls"
                          type="button"
                          onClick={() => openSign({
                            plan_id: sessionsPlanRow.plan_id,
                            employee_id: sessionsPlanRow.employee_id,
                            session_id: s.id,
                            session_date: s.session_date ? String(s.session_date).slice(0, 10) : '',
                            topic: sessionsPlanRow.topic
                          })}
                          style={{
                            padding: '.45rem .75rem',
                            fontSize: 12,
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 600,
                            border: '1px solid var(--border-color)',
                            background: 'var(--bg-tertiary)',
                            color: 'var(--text-primary)'
                          }}
                        >
                          {s.signature_data ? '重签' : '签名'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: '#64748b' }}>未达标</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </WideModal>

      <WideModal
        isOpen={signOpen}
        onClose={() => {
          if (signSaving) return;
          setSignOpen(false);
          setSigningRow(null);
        }}
        title={`手写签名：${signingRow?.topic || ''} ${signingRow?.session_date ? `· ${signingRow.session_date}` : ''}`}
        size="md"
        bodyStyle={{ maxHeight: '75vh' }}
      >
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
          请在下方签名区域手写签名（手机端支持手指触控）。
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 8, background: '#fff' }}>
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            style={{ touchAction: 'none', display: 'block', width: '100%', height: 220, borderRadius: 8 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <button
            type="button"
            onClick={clearCanvas}
            disabled={signSaving}
            style={{
              padding: '.55rem 1rem',
              fontSize: 13,
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 500,
              border: '1px solid var(--border-color)',
              background: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }}
          >
            清空
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={saveSignature}
            disabled={signSaving}
            style={{
              padding: '.55rem 1rem',
              fontSize: 13,
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              border: '1px solid var(--brand-primary)',
              background: 'var(--brand-primary)',
              color: '#fff'
            }}
          >
            {signSaving ? '保存中...' : '保存签名'}
          </button>
        </div>
      </WideModal>

      <p style={hintStyle}>说明：本页仅展示与本人（employee_id）关联的“培训计划-参培人员”记录；学时可按多次登记累计。</p>
    </div>
  );
}
